import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, MapPin, CalendarDays, ShieldCheck, GraduationCap, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeaderProps {
  className?: string;
}

const navigationItems = [
  { title: 'Find Your Club', href: '/find-club', icon: MapPin },
  { title: 'Events', href: '/events', icon: CalendarDays },
  { title: 'Sign In', href: '/auth', icon: ShieldCheck },
];

export function Header({ className }: HeaderProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getContextualCTA = () => {
    if (location.pathname === '/') {
      return (
        <Button 
          asChild 
          className="bg-gradient-to-r from-amber-400 to-orange-500 text-foreground hover:from-amber-500 hover:to-orange-600 font-semibold rounded-full px-6 shadow-md"
        >
          <Link to="/find-club">Sign Up</Link>
        </Button>
      );
    } else if (location.pathname.startsWith('/coach') || location.pathname.startsWith('/admin')) {
      return (
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/">Back to Home</Link>
        </Button>
      );
    }
    return null;
  };

  return (
    <>
      <header className={cn(
        "bg-background/95 backdrop-blur-lg border-b border-border/50 sticky top-0 z-50",
        className
      )}>
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img 
                src="https://lztlmniinqehhxbqftsn.supabase.co/storage/v1/object/public/badge-icons/FutWork_Primary%20Logo-01.png" 
                alt="FutWork Logo" 
                className="h-7 md:h-9"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-full transition-all",
                    isActive(item.href) 
                      ? "bg-muted text-foreground" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  {item.title}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex">
              {getContextualCTA()}
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden rounded-full h-9 w-9"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Full-Screen Overlay Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop blur */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-md animate-fade-in"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Panel — slides in from right */}
          <div
            className="absolute top-0 right-0 h-full w-[85%] max-w-xs flex flex-col animate-slide-in-right"
            style={{ background: 'hsla(224, 54%, 10%, 0.92)', backdropFilter: 'blur(24px)' }}
          >
            {/* Close */}
            <div className="flex items-center justify-between px-5 h-14 border-b border-white/10">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/40">Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition-transform"
              >
                <X className="h-4 w-4 text-white/70" />
              </button>
            </div>

            {/* Nav Items */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.title}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all active:scale-[0.97]",
                      active
                        ? "bg-gradient-to-r from-amber-400/15 to-orange-500/10 text-amber-400"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    <Icon className={cn("h-4.5 w-4.5 shrink-0", active ? "text-amber-400" : "text-white/40")} strokeWidth={2} />
                    {item.title}
                  </Link>
                );
              })}
            </nav>

            {/* Bottom CTA */}
            <div className="p-4 border-t border-white/10">
              <Button
                asChild
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-foreground hover:from-amber-500 hover:to-orange-600 font-semibold rounded-full h-12 text-sm shadow-lg shadow-amber-500/20 active:scale-[0.98] uppercase tracking-wider"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Link to="/find-club">
                  Sign Up
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}